import {
  type AttributionAddToCartInput,
  attributionFindLatestMatchingItem,
  attributionGetCappedItems,
} from '../attribution'

import {
  type RecommendationAttributionContext,
  recommendationAttributionGetProductType,
} from './recommendationAttributionProductType'
import {
  type RecommendationAttributionCartItem,
  type RecommendationAttributionClick,
  type RecommendationAttributionProductType,
  recommendationAttributionRead,
  type RecommendationAttributionStorage,
  recommendationAttributionWrite,
} from './recommendationAttributionStorage'

type RecommendationAttributionClickInput = Omit<
  RecommendationAttributionClick,
  'clickId' | 'createdAt' | 'productId'
> &
  RecommendationAttributionContext

export type RecommendationAttributionAddToCartInput = AttributionAddToCartInput

/**
 * Records a recommendation interaction and derives its product type when the caller does not provide one.
 */
export const recommendationAttributionRecordClick = ({
  areaId,
  brandName,
  interactionType,
  offerId,
  productId,
  productType,
  sellerId,
  serviceId,
}: RecommendationAttributionClickInput): void => {
  if (!productId || !areaId || !serviceId) {
    return
  }

  const storage = recommendationAttributionRead()
  const resolvedProductType =
    productType ??
    recommendationAttributionGetProductType({ brandName, sellerId })

  recommendationAttributionWrite({
    ...storage,
    clicks: attributionGetCappedItems([
      ...storage.clicks,
      {
        areaId,
        clickId: crypto.randomUUID(),
        createdAt: Date.now(),
        interactionType,
        ...(offerId && { offerId }),
        productId,
        ...(resolvedProductType && { productType: resolvedProductType }),
        serviceId,
      },
    ]),
  })
}

const findLatestMatchingClick = ({
  offerId,
  productFamilyIds,
  productId,
  storage,
}: {
  offerId?: string
  productFamilyIds?: string[]
  productId?: string
  storage: RecommendationAttributionStorage
}): RecommendationAttributionClick | undefined => {
  return attributionFindLatestMatchingItem(storage.clicks, {
    offerId,
    productFamilyIds,
    productId,
  })
}

const getAttributedCartItem = (
  {
    offerId,
    productFamilyIds,
    productId,
    quantity,
  }: RecommendationAttributionAddToCartInput,
  storage: RecommendationAttributionStorage,
): RecommendationAttributionCartItem | undefined => {
  if (!offerId && !productId) {
    return undefined
  }

  const click = findLatestMatchingClick({
    offerId,
    productFamilyIds,
    productId,
    storage,
  })

  if (!click) {
    return undefined
  }

  const resolvedProductId = productId ?? click.productId
  const resolvedOfferId = offerId ?? click.offerId
  const resolvedProductType: RecommendationAttributionProductType | undefined =
    click.productType

  return {
    areaId: click.areaId,
    clickId: click.clickId,
    createdAt: Date.now(),
    ...(resolvedOfferId && { offerId: resolvedOfferId }),
    productId: resolvedProductId,
    ...(resolvedProductType && { productType: resolvedProductType }),
    quantity,
    serviceId: click.serviceId,
  }
}

/**
 * Attributes all added products from one storage snapshot and persists them together.
 * The returned array stays aligned with the input products so callers can enrich
 * one generic add-to-cart event per product without sending a second event.
 */
export const recommendationAttributionRecordProductsAddToCart = (
  products: RecommendationAttributionAddToCartInput[],
): (RecommendationAttributionCartItem | undefined)[] => {
  const storage = recommendationAttributionRead()
  const attributedCartItems = products.map((product) =>
    getAttributedCartItem(product, storage),
  )
  const cartItems = attributedCartItems.filter(
    (cartItem): cartItem is RecommendationAttributionCartItem =>
      Boolean(cartItem),
  )

  if (cartItems.length > 0) {
    recommendationAttributionWrite({
      ...storage,
      cartItems: attributionGetCappedItems([
        ...storage.cartItems,
        ...cartItems,
      ]),
    })
  }

  return attributedCartItems
}

/**
 * Attributes an add-to-cart to the latest matching recommendation click and stores the result.
 */
export const recommendationAttributionRecordAddToCart = (
  product: RecommendationAttributionAddToCartInput,
): RecommendationAttributionCartItem | undefined => {
  return recommendationAttributionRecordProductsAddToCart([product])[0]
}
